import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { DatePicker, ConfigProvider } from 'antd';
import { Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import esES from 'antd/locale/es_ES';
import enUS from 'antd/locale/en_US';
import itIT from 'antd/locale/it_IT';
import { colors } from '../../../themes/typography';

/**
 * ResolutionFlow component - Step 3 of alert flow
 * Shows scheduled date with option to edit and final "Mark as Repaired" action
 */
const ResolutionFlow = ({ 
  scheduledDate,
  onEditDate,
  onMarkRepaired,
  loading = false,
  t 
}) => {
  const { i18n } = useTranslation();
  const locale = { es: esES, en: enUS, it: itIT }[i18n.language] || esES;
  
  const [showEditDate, setShowEditDate] = React.useState(false);
  const [editingDate, setEditingDate] = React.useState(scheduledDate);

  const handleEditSubmit = () => {
    onEditDate(editingDate);
    setShowEditDate(false);
  };

  const handleEditCancel = () => {
    setEditingDate(scheduledDate);
    setShowEditDate(false);
  };

  if (showEditDate) {
    return (
      <Box sx={{
        p: '12px',
        backgroundColor: 'rgba(249, 250, 251, 0.8)',
        borderRadius: '6px',
        border: '1px solid rgba(107, 114, 128, 0.2)',
        mt: '8px'
      }}>
        <Typography sx={{
          fontSize: '10px',
          color: colors.text.primary,
          fontWeight: 500,
          textAlign: 'center',
          mb: '8px',
          lineHeight: 1.2
        }}>
          {t ? t('editDate') : 'Edit Date'}
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          mb: '8px'
        }}>
          <ConfigProvider locale={locale}>
            <DatePicker
              value={editingDate}
              onChange={setEditingDate}
              size="small"
              format={i18n.language === 'es' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
              style={{
                borderColor: 'rgba(107, 114, 128, 0.3)',
                borderRadius: '4px',
                fontSize: '10px'
              }}
            />
          </ConfigProvider>
        </Box>

        <Box sx={{
          display: 'flex',
          gap: '6px',
          justifyContent: 'center'
        }}>
          <Button
            onClick={handleEditCancel}
            sx={{
              minWidth: 'auto',
              px: '8px',
              py: '2px',
              fontSize: '10px', // Match experiment card
              fontWeight: 500,
              color: '#8E8E93', // Apple gray
              backgroundColor: 'transparent',
              border: '1px solid rgba(142, 142, 147, 0.2)',
              borderRadius: '6px', // More rounded
              textTransform: 'none',
              transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              '&:hover': {
                backgroundColor: 'rgba(142, 142, 147, 0.08)',
                borderColor: 'rgba(142, 142, 147, 0.3)'
              }
            }}
          >
            {t ? t('cancel') : 'Cancel'}
          </Button>

          <Button
            onClick={handleEditSubmit}
            disabled={!editingDate}
            sx={{
              minWidth: 'auto',
              px: '12px',
              py: '2px',
              fontSize: '10px', // Match experiment card
              fontWeight: 500, // Match experiment card
              color: 'white',
              backgroundColor: '#007AFF', // Apple blue
              border: '1px solid #007AFF',
              borderRadius: '6px', // More rounded
              textTransform: 'none',
              transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              '&:hover': {
                backgroundColor: '#0051D0',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(0, 122, 255, 0.25)'
              },
              '&:disabled': {
                opacity: 0.4
              }
            }}
          >
            {t ? t('save') : 'Save'}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      p: '12px',
      backgroundColor: 'rgba(249, 250, 251, 0.8)',
      borderRadius: '6px',
      border: '1px solid rgba(107, 114, 128, 0.2)',
      mt: '8px'
    }}>

      {/* Scheduled date with edit option */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        mb: '8px'
      }}>
        <Typography sx={{
          fontSize: '10px', // Match experiment card
          color: colors.text.primary,
          fontWeight: 500,
          lineHeight: 1.2
        }}>
          {t ? t('scheduled') : 'Scheduled'}: {scheduledDate.format(i18n.language === 'es' ? 'DD/MM/YYYY' : 'MM/DD/YYYY')}
        </Typography>

        <IconButton
          onClick={() => setShowEditDate(true)}
          disabled={loading}
          sx={{
            width: '16px',
            height: '16px',
            backgroundColor: 'transparent',
            border: '1px solid rgba(107, 114, 128, 0.2)',
            borderRadius: '3px',
            transition: 'all 0.15s ease',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.05)'
            },
            '&:disabled': {
              opacity: 0.4
            }
          }}
        >
          <Edit sx={{ fontSize: '8px', color: '#6b7280' }} />
        </IconButton>
      </Box>

      {/* Mark as repaired button */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Button
          onClick={() => onMarkRepaired(editingDate || scheduledDate)}
          disabled={loading}
          sx={{
            minWidth: 'auto',
            px: '16px',
            py: '4px',
            fontSize: '10px',
            fontWeight: 500, // Match experiment card
            color: 'white',
            backgroundColor: '#34C759', // Apple green for final success action
            border: '1px solid #34C759',
            borderRadius: '8px', // More rounded for Apple feel
            textTransform: 'none',
            transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Apple easing
            '&:hover': {
              backgroundColor: '#30B94D',
              borderColor: '#30B94D',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(52, 199, 89, 0.25)'
            },
            '&:disabled': {
              opacity: 0.4,
              backgroundColor: '#34C759',
              transform: 'none',
              boxShadow: 'none'
            }
          }}
        >
          {loading ? 
            (t ? t('markingRepaired') : 'Marking as repaired...') : 
            (t ? t('markAsRepaired') : 'Mark as Repaired')
          }
        </Button>
      </Box>
    </Box>
  );
};

export default ResolutionFlow;